import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchNewsletterCategories, subscribeNewsletter } from '../lib/marketingApi';

// Public newsletter signup with category selection. `source` tags where the contact came from.
export default function NewsletterSignup({ source = 'newsletter', className = '' }) {
  const { lang } = useLanguage();
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { fetchNewsletterCategories().then((d) => setCats(d.categories || [])).catch(() => {}); }, []);

  const toggle = (slug) => setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { toast.error('Please enter a valid email'); return; }
    setLoading(true);
    try {
      await subscribeNewsletter({ email, name: name || undefined, categories: selected, source, language: lang });
      setDone(true);
      toast.success('Subscribed — check your inbox for our next edition.');
    } catch (err) { toast.error('Could not subscribe. Please try again.'); } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className={`flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.03] ${className}`} data-testid="newsletter-signup-done">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand"><CheckCircle2 className="h-6 w-6" /></span>
        <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">You're subscribed</h3>
        <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">We'll send you curated weekly market updates. You can unsubscribe anytime from any email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-signup" className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-8 ${className}`}>
      <div className="flex items-center gap-2 text-brand"><Mail className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Newsletter</span></div>
      <h3 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">Weekly market updates, curated</h3>
      <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">Choose the topics you care about. We'll send a concise, AI-curated digest of what moved the market — no spam.</p>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="newsletter-categories">
        {cats.map((c) => {
          const on = selected.includes(c.slug);
          return (
            <button type="button" key={c.slug} onClick={() => toggle(c.slug)} data-testid={`nl-cat-${c.slug}`}
              title={c.description}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${on ? 'border-brand bg-brand text-white' : 'border-slate-300 text-slate-600 hover:border-brand hover:text-brand dark:border-white/15 dark:text-slate-300'}`}>
              {c.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-400">Leave all unselected to receive every category.</p>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" data-testid="nl-signup-name"
          className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="nl-signup-email"
          className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none focus:border-brand dark:border-white/15 dark:bg-white/[0.04] dark:text-white" />
      </div>
      <button type="submit" disabled={loading} data-testid="nl-signup-submit"
        className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Subscribing…</> : 'Subscribe'}
      </button>
    </form>
  );
}
